import { DataRoomRecipientKind } from './data-room.enums';

/**
 * 20.1.0 §1: only two identities generate trackable activity — Stakeholders and External Guests.
 * Admins, Contributors and Guest Users are excluded from every Activity metric, a Guest
 * Contributor with the Data Rooms toggle on included: elevated access does not make an internal
 * user's activity countable.
 */
export enum DataRoomActivityActorType {
  EXTERNAL_GUEST = 'external-guest',
  STAKEHOLDER = 'stakeholder',
}

/**
 * 20.1.0 §6–7. V1 has no preview, so both hand the file over; what separates them is the control
 * that was pressed, not what happened to the file afterwards.
 */
export enum DataRoomActivityEventType {
  DOWNLOAD = 'download',
  VIEW = 'view',
}

/**
 * 20.1.0 §7: a bulk download logs one event per file it carries, tagged with what was asked for.
 * Values are kebab-case per this repo's enum convention; the specification spells them with
 * underscores.
 */
export enum DataRoomDownloadScope {
  DATA_ROOM_BULK = 'data-room-bulk',
  FOLDER_BULK = 'folder-bulk',
  INDIVIDUAL = 'individual',
}

/**
 * The visitor an access row stands for.
 *
 * An internal recipient is a countable Stakeholder by construction: the share candidate list is
 * restricted to `role = stakeholder`, no `accessRole`, and not an external guest — the three
 * conditions 20.1.0 §1 excludes. Widening that list would have to widen this with it.
 */
export const dataRoomActivityActorType = (
  recipientKind: DataRoomRecipientKind,
): DataRoomActivityActorType =>
  recipientKind === DataRoomRecipientKind.EXTERNAL
    ? DataRoomActivityActorType.EXTERNAL_GUEST
    : DataRoomActivityActorType.STAKEHOLDER;

/**
 * A visitor's type from whichever of the two sources has it, read as text from the database.
 *
 * The journal snapshots the type on every row and answers even for a grant that has since been
 * deleted, so it goes first. Somebody who has been in a room and opened nothing inside it has no
 * journal row at all, and their grant records the recipient kind instead — mapped by the rule
 * above rather than by a second one, so the Type column cannot come to mean two different things
 * on two screens.
 */
export const visitorActorType = ({
  actorType,
  recipientKind,
}: {
  actorType: null | string;
  recipientKind: null | string;
}): DataRoomActivityActorType | null => {
  const journalled = Object.values(DataRoomActivityActorType).find((type) => type === actorType);

  if (journalled) return journalled;

  const granted = Object.values(DataRoomRecipientKind).find((kind) => kind === recipientKind);

  return granted ? dataRoomActivityActorType(granted) : null;
};
